import React, {ReactElement, useEffect, useState} from "react";
import styles from "./map-filter-panel.module.scss";
import SearchBar from "../../../ui-kit/search-bar/search-bar";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import useDebounce from "../../../../hooks/useDebounce";
import TypeOfObjectCard, {TypeCases} from "./type-of-objects-card/type-of-objects-card";
import CustomScrollbar from "../../../ui-kit/scrollbar/scrollbar";
import { useTranslation } from 'react-i18next';
import {
    clearFilters,
    setFilter as setFilterSearchQueryAll
} from "../../../../services/reducers/filters";
import ClosedFilterCard from "../closed-filter-card/closed-filter-card";

const MapFilterPanel = React.memo(function MapFilterPanel(): ReactElement {
    const { t } = useTranslation();
    const [searchInputValue, setSearchInputValue] = useState<string>("");
    const [showResults, setShowResults] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const debouncedSearchValue = useDebounce(searchInputValue, 300);
    const setFilter = (value: string = "") => {
        dispatch(setFilterSearchQueryAll({ filters: { value, vacant: true } }));
    };
    const { plots, investmentOpportunities, spacesForRent} = useAppSelector(state => state.filters)

    useEffect(() => {
        if (debouncedSearchValue.trim() !== "") {
            setFilter(debouncedSearchValue);
            setShowResults(true);
        } else {
            setFilter();
            setShowResults(false);
        }
    }, [debouncedSearchValue, dispatch]);

    const handleToggleLandPlots = () => {
        navigate("plt");
    };

    const handleToggleInvestmentOpportunities = () => {
        navigate("invop");
    };

    const handleToggleSpacesForRent = () => {
        navigate("spc");
    };

    const handleClearSearch = () => {
        setSearchInputValue("");
        dispatch(clearFilters({}));
        setShowResults(false);
    };
    return (
        <div className={styles.outerContainer} style={{position: "relative", height: "100%"}}>
            <div className={styles.container}>
                <CustomScrollbar height='100%'>
                    {location.pathname.includes("/search/") && location.pathname !== "/search" ? (
                        <Outlet/>
                    ) : (
                        <>
                            <div className={styles.search}>
                                <label className={styles.searchCaption}>
                                    <span>{t('mapFilter.exploreBestOption')}</span>
                                </label>
                                <SearchBar
                                    searchValue={searchInputValue}
                                    onSearchChange={e => setSearchInputValue(e.target.value)}
                                    onSearch={() => {}}
                                    onClearSearch={handleClearSearch}
                                    placeholder={t('mapFilter.searchPlaceholder')}
                                />
                            </div>
                            {showResults ? (
                                <>
                                    <div className={styles.resultsHeader}>
                                        <label className={styles.label}>{t('mapFilter.landPlots')}</label>
                                        <span className={styles.found}>
                                        {plots.filteredData.length} {t('mapFilter.found')}
                                    </span>
                                    </div>
                                    <div className={styles.cardsContainer}>
                                        {plots.filteredData.map((plot, index) => (
                                            <ClosedFilterCard type='Plots' key={index} data={plot} />
                                        ))}
                                    </div>
                                    <div className={styles.resultsHeader}>
                                        <label className={styles.label}>{t('mapFilter.spacesForRent')}</label>
                                        <span className={styles.found}>
                                        {spacesForRent.filteredData.length} {t('mapFilter.found')}
                                    </span>
                                    </div>
                                    <div className={styles.cardsContainer}>
                                        {spacesForRent.filteredData.map((spaceForRent, index) => (
                                            <ClosedFilterCard type='SpacesForRent' key={index} data={spaceForRent} />
                                        ))}
                                    </div>
                                    <div className={styles.resultsHeader}>
                                        <label className={styles.label}>{t('mapFilter.investmentOpportunities')}</label>
                                        <span className={styles.found}>{investmentOpportunities.filteredData.length} {t('mapFilter.found')}</span>
                                    </div>
                                    <div className={styles.cardsContainer}>
                                        {investmentOpportunities.filteredData.map((opportunity, index) => (
                                            <ClosedFilterCard type='InvestmentOpportunities' key={index} data={opportunity} />
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.filters}>
                                    <label className={styles.filtersCaption}>
                                        <span>{t('mapFilter.chooseInvestingObject')}</span>
                                    </label>
                                    <div className={styles.filtersItems}>
                                        <TypeOfObjectCard typeCase={TypeCases.Lands} onClick={handleToggleLandPlots} value={plots.data.filter(item => item.status?.toLowerCase() === "vacant").length}/>
                                        <TypeOfObjectCard typeCase={TypeCases.Spaces} onClick={handleToggleSpacesForRent} value={spacesForRent.data.filter(item => item.is_inv_lan == 1).length}/>
                                        <TypeOfObjectCard typeCase={TypeCases.Investment} onClick={handleToggleInvestmentOpportunities} value={investmentOpportunities.data.filter(item => item.status?.toLowerCase() === "vacant").length}/>
                                    </div>
                                </div>
                            )}

                        </>
                    )}
                </CustomScrollbar>
            </div>
        </div>
    );
})

export default MapFilterPanel;
